import { useEffect, useState } from "react";
import Layout from "./layout";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addSelectedPrestation, clearSelectedPrestations, fetchReservationById } from "../../store/slices/rendezVousSlice";
import { fetchEtablissementById, setSelectedEtablissement } from "../../store/slices/etablissementSlice";
import Confirmation from "../../components/confirmation";
import ConfirmBloc from "./confirmBloc";
import PrestationsConfirmBloc from "./prestationConfirmPage";
import { Helmet, HelmetProvider } from "react-helmet-async";
import TopHeader from "../../components/TopHeader";
import Footer from "../../components/Footer";
import Skeleton from "react-loading-skeleton";

const ConfirmPageRendezVous = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const etablissement = useSelector(
    (state) => state.etablissements.selectedEtablissement
  );
  const { selectedPrestations, currentReservation, currentReservationStatus } =
    useSelector((state) => state.rendezvous);

  useEffect(() => {
    dispatch(fetchReservationById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (
      currentReservationStatus === "succeeded" &&
      currentReservation?.etablissement_id
    ) {
      dispatch(fetchEtablissementById(currentReservation.etablissement_id));
    }
  }, [currentReservationStatus, currentReservation, dispatch]);

  const buttonAction = (prestation) => {
    dispatch(clearSelectedPrestations());
    dispatch(addSelectedPrestation(prestation));
    dispatch(setSelectedEtablissement(etablissement));
    navigate(`/details-rdv/${etablissement.id}`);
  };

  const addToGoogleCalendar = () => {
    if (!currentReservation || !etablissement) return;
    
    const startDate = new Date(currentReservation.start_time);
    const endDate = new Date(currentReservation.end_time);
    
    const formatDate = (date) => {
      const pad = (n) => n < 10 ? '0' + n : n;
      return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
    };

    const startTime = formatDate(startDate);
    const endTime = formatDate(endDate);
    
    const title = `Rendez-vous ${etablissement?.nom}`;
    const description = `Prestations: ${currentReservation?.prestations_snapshot?.map(p => p.name).join(', ')}\n\nNotes: ${currentReservation.notes || 'Aucune note'}`;
    const location = etablissement?.adresse_postale || '';
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const googleCalendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startTime}/${endTime}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}&ctz=${encodeURIComponent(timeZone)}`;

    window.open(googleCalendarUrl, '_blank');
  };

  // Afficher le skeleton pendant le chargement
  if (currentReservationStatus === "loading") {
    return (
      <Layout
        currentStep={3}
        etablissement={null}
        prestation={[]}
        recap={true}
        from="facturation"
        conf={false}
        frais={false}
        greyBg={true}
        identification={false}
      >
        <HelmetProvider>
          <Helmet>
            <title>Chargement...</title>
          </Helmet>
        </HelmetProvider>

        {/* Main Content Area */}
        <div className="space-y-40 container">
          {/* ConfirmBloc Area */}
          <div className="confirm-bloc-skeleton">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <Skeleton width={300} height={32} className="mb-4" />
              <Skeleton count={3} className="mb-6" />
              <div className="flex gap-3">
                <Skeleton width={200} height={44} />
                <Skeleton width={200} height={44} />
              </div>
            </div>
          </div>

          {/* Etablissement and Prestations Area */}
          <div className="content-skeleton">
            {/* Etablissement Info */}
            <div className="etablissement-info mb-8">
              <div className="details-left flex-wrap d-flex gap-4 mb-4">
                <Skeleton width={100} height={20} />
                <Skeleton width={200} height={20} />
                <Skeleton width={150} height={20} />
              </div>
              <div className="service-badges mt-8 flex gap-2">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="service-badge">
                    <Skeleton width={120} height={30} />
                  </div>
                ))}
              </div>
            </div>

            {/* Prestations List */}
            <div className="prestations-list">
              <Skeleton width={250} height={28} className="mb-6" />
              <div className="space-y-4">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="flex justify-between items-center p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center gap-4">
                      <Skeleton width={50} height={50} circle />
                      <div>
                        <Skeleton width={180} height={20} className="mb-2" />
                        <Skeleton width={120} height={16} />
                      </div>
                    </div>
                    <Skeleton width={100} height={36} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Afficher l'erreur si le chargement a échoué ou si aucune réservation n'est trouvée
  if (currentReservationStatus === "failed" || !currentReservation) {
    return (
      <>
        <TopHeader/>
        <div className="container h-100vh">
          <h3 className="grey500 mt-32">Une erreur est survenue : vous n'êtes pas autorisé à accéder à cette page.</h3>
          <p className="body grey500 mt-16">
            Vous recevrez un e-mail si votre rendez-vous est confirmé, ou vous pouvez vérifier directement dans votre espace compte
          </p> 
        </div>
        <Footer/>
      </>
    );
  }

  // Afficher le contenu principal lorsque les données sont chargées
  return (
    <Layout
      currentStep={3}
      etablissement={etablissement}
      prestation={currentReservation?.prestations_snapshot}
      recap={true}
      from="facturation"
      conf={false}
      frais={false}
      greyBg={true}
      identification={false}
    >
      <HelmetProvider>
        <Helmet>
          <title>Page confirmation réservation</title>
          <meta
            name="description"
            content="confirmation réservation"
          />
        </Helmet>
      </HelmetProvider>
      
      <ConfirmBloc
        title={!etablissement ?  <Skeleton width="100px" height={20} />:etablissement?.immediat ? "Votre rendez-vous est confirmé !" : "Votre demande a été envoyée au prestataire !"}
        text={!etablissement ?  <Skeleton width="100px" height={20} />:etablissement?.immediat ?
          "Vous recevrez un email de validation avec un récapitulatif. Vous pouvez modifier ou annuler votre rendez-vous jusqu'à 48h à l'avance."
          :
          "Le prestataire doit d'abord accepter valider votre rendez-vous. Vous recevrez un email de validation avec un récapitulatif. Vous pouvez modifier ou annuler votre rendez-vous jusqu'à 48h à l'avance."
        }
        btnText="Ajouter le rdv à mon agenda"
        clickPrimary={addToGoogleCalendar}
        ctaLink={"/compte/rendez-vous"}
        ctatext={"Voir mes demandes"}
      />
      
      <div className="mt-40">
        <PrestationsConfirmBloc buttonAction={buttonAction}/>
      </div>
    </Layout>
  );
};

export default ConfirmPageRendezVous;